const deepmerge = require('deepmerge')
const DEFAULTS = require('./defaultConfig.json')

class Ticket {
  constructor (type, ticket) {
    this._type = type
    this.settings = ticket
  }

  get type () {
    return this._type
  }

  get description () {
    return typeof this.settings.description === 'string' ? this.settings.description : ''
  }

  get label () {
    return this.settings.label
  }

  get dropdown () {
    return this.settings.dropdown
  }

  get min () {
    return this.settings.min
  }

  get max () {
    return this.settings.max
  }

  hasRatio (ticketType) {
    if (!ticketType || !this._hasRatios()) {
      return
    }

    const ticketRatio = this.settings.ratios[ticketType]

    return typeof ticketRatio !== 'undefined'
  }

  _hasProperty (property) {
    return this.settings.hasOwnProperty(property) && typeof this.settings[property] === 'object'
  }

  ratio (ticketType) {
    if (!ticketType || !this._hasProperty('ratios')) {
      return
    }

    const ticketRatio = this.settings.ratios[ticketType]

    return ticketRatio
  }

  /**
   * Returns all ratio limits for the ticket
   * @returns {object}
   */
  ratios () {
    if (this._ratios) {
      return this._ratios
    }
    if (!this._hasProperty('ratios')) {
      return []
    }
    this._ratios = Object.keys(this.settings.ratios).map(r => {
      return Object.assign({ type: r }, this.settings.ratios[r])
    })
    return this._ratios
  }

  artPassPriceTypes () {
    return this.priceTypes().filter(p => p.type === 'artPass' || p.type === 'artPassWithDonation')
  }

  matchingArtPass (priceType) {
    let artPass
    if (priceType === 'regular') {
      artPass = this.priceTypes().find(p => p.type === 'artPass')
    }
    if (priceType === 'withDonation') {
      artPass = this.priceTypes().find(p => p.type === 'artPassWithDonation')
    }
    if (priceType === 'annualPass') {
      artPass = this.priceTypes().find(p => p.type === 'artPassAnnual')
    }
    return artPass
  }

  tablePriceTypes () {
    if (this._tablePriceTypes) {
      return this._tablePriceTypes
    }
    this._tablePriceTypes = this.priceTypes().filter(p => p.type !== 'artPass' && p.type !== 'artPassWithDonation' && p.type !== 'artPassAnnual')
    return this._tablePriceTypes
  }

  priceTypes () {
    if (this._priceTypes) {
      return this._priceTypes
    }
    this._priceTypes = Object.keys(this.settings.priceTypes).map(p => {
      return { type: p, id: this.settings.priceTypes[p] }
    })
    return this._priceTypes
  }

  priceId (priceType) {
    if (!priceType || !this._hasProperty('priceTypes')) {
      return
    }

    return this.settings.priceTypes[priceType]
  }
}

export default class Table {
  constructor (json) {
    if (json) {
      this.userSuppliedSettings = true
    }

    // Take the user submitted settings and merge with the defaults that we have configured
    this.settings = deepmerge(DEFAULTS, json || {})
  }

  static fromElement (elementSelector) {
    if (!elementSelector) {
      throw new Error('No elementSelector provided to the override object')
    }

    const settingsElement = $(elementSelector)
    let settingsJson

    if (settingsElement.length > 0 && settingsElement.html().trim().length > 0) {
      try {
        settingsJson = JSON.parse(settingsElement.html())
      } catch (e) {
        this._error = e
        console.warn('The settings override appears incorrectly formatted. Please correct the issue in the Tessitura control panel. The page will be rendered with default settings.')
        console.error(e)
      }
    }

    return new Table(settingsJson)
  }

  get settingsElementExists () {
    if (this.settings.debugMode === true) {
      return true
    }
    return this.userSuppliedSettings === true
  }

  get debug () {
    return this.settings.debugMode === true
  }

  get error () {
    return this._error
  }

  get showFamilyDiscount () {
    return this.settings.checkout.showFamilyDiscountBanner
  }

  get hasFamilyDiscount () {
    return this.settings.checkout.familyDiscountEnabled
  }

  get showArtPassCheckbox () {
    return this.settings.checkout.showArtPassCheckbox
  }

  get title () {
    return this.settings.table.title || ''
  }

  headers () {
    if (this._headers) {
      return this._headers
    }
    this._headers = Object.keys(this.settings.table.headers).map(h => this.settings.table.headers[h])

    return this._headers
  }

  /**
   * Get a list of all ticket objects not including the default
   * @returns {Array<Ticket>} - Tickets exclusive of default
   */
  tickets () {
    if (this._tickets) {
      return this._tickets
    }
    this._tickets = Object.keys(this.settings.tickets).reduce((tickets, t) => {
      if (t !== 'default') {
        tickets.push(new Ticket(t, this.settings.tickets[t]))
      }
      return tickets
    }, [])

    return this._tickets
  }

  /**
   * Get the settings for a specific ticket type. If no ticket type supplied, returns default
   * @param  {(string|number)} [ticketType] - The ticket ID
   * @returns {object} - Default setting or the user settings merged with user supplied default
   */
  ticket (ticketType) {
    if (!ticketType) {
      return new Ticket('default', this.settings.tickets.default)
    }

    const ticketSetting = this.settings.tickets[ticketType]

    if (typeof ticketSetting === 'undefined') {
      return new Ticket(ticketType, this.settings.tickets.default)
    } else {
      return new Ticket(ticketType, deepmerge(this.settings.tickets.default, ticketSetting))
    }
  }
}
